"use client";

import { useQuery } from "@tanstack/react-query";
import {
	Bot,
	ChevronRight,
	CreditCard,
	DollarSign,
	History,
	Home,
	Image as ImageIcon,
	Images,
	LayoutDashboard,
	LogOut,
	Mail,
	Settings,
	User as UserIcon,
	Users,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NavBar } from "@/components/ui/navbar";
import { authClient } from "@/lib/auth-client";
import { orpc } from "@/orpc/react-query";
import type { User } from "@/server/db/schema/user";

const items = [
	{ name: "Home", url: "/", icon: Home },
	{ name: "Studio", url: "/studio", icon: Bot },
	{ name: "Examples", url: "/examples", icon: ImageIcon },
	{ name: "Pricing", url: "/pricing", icon: DollarSign },
	{ name: "Contact", url: "/contact", icon: Mail },
];

const adminItems = [
	{ name: "Dashboard", url: "/admin", icon: LayoutDashboard },
	{ name: "Users", url: "/admin/users", icon: Users },
	{ name: "Images", url: "/admin/images", icon: Images },
];

function UserDropdown({
	user,
}: {
	user: User;
}) {
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" className="relative h-10 w-10 rounded-full">
					<Avatar className="h-10 w-10">
						<AvatarImage
							src={user.image || undefined}
							alt={user.name || "User avatar"}
						/>
						<AvatarFallback>
							{user.name
								?.split(" ")
								.map((n: string) => n[0])
								.join("")
								.toUpperCase() || "U"}
						</AvatarFallback>
					</Avatar>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="w-56" align="end" forceMount>
				<DropdownMenuLabel className="font-normal">
					<div className="flex flex-col space-y-1">
						<p className="text-sm font-medium leading-none">{user.name}</p>
						<p className="text-xs leading-none text-muted-foreground">
							{user.email}
						</p>
					</div>
				</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<DropdownMenuGroup>
					<DropdownMenuItem asChild>
						<Link href="/studio" className="flex items-center">
							<UserIcon className="mr-2 h-4 w-4" />
							<span>Studio</span>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link href="/studio/history" className="flex items-center">
							<History className="mr-2 h-4 w-4" />
							<span>History</span>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link href="/pricing" className="flex items-center">
							<CreditCard className="mr-2 h-4 w-4" />
							<span>Credits: {user.credits || 0}</span>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild disabled>
						<Link href="/settings" className="flex items-center">
							<Settings className="mr-2 h-4 w-4" />
							<span>Settings</span>
						</Link>
					</DropdownMenuItem>
				</DropdownMenuGroup>
				<DropdownMenuSeparator />
				<DropdownMenuItem
					className="text-red-600 focus:text-red-600 focus:bg-red-50"
					onClick={() => authClient.signOut()}
				>
					<LogOut className="mr-2 h-4 w-4" />
					<span>Log out</span>
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

function StartCreatingButton() {
	return (
		<Link href="/studio" passHref>
			<Button
				size="lg"
				className="bg-black hover:bg-gray-900 text-white h-12 flex items-center justify-center gap-2 rounded-xl"
			>
				Start Creating <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
			</Button>
		</Link>
	);
}

export function HeaderMobile() {
	const { data: user } = useQuery(orpc.user.getMe.queryOptions({}));

	return (
		<div className="relative md:hidden py-6 px-4 flex items-center justify-between">
			<Image
				src="/funart.webp"
				alt="FunArt"
				width={46}
				height={46}
				className="rounded-xl shadow-lg"
			/>
			<NavBar items={items} />
			{user ? <UserDropdown user={user as User} /> : <StartCreatingButton />}
		</div>
	);
}

export function HeaderDesktop() {
	const pathname = usePathname();

	const { data: user } = useQuery(orpc.user.getMe.queryOptions({}));
	const isAdmin = user?.role === "admin";

	const isAdminPanel = pathname.startsWith("/admin");

	const navItems = isAdminPanel
		? adminItems
		: [
				...items,
				...(isAdmin
					? [{ name: "Admin", url: "/admin", icon: LayoutDashboard }]
					: []),
			];

	return (
		<header className="hidden md:flex h-16 items-center justify-between border-b bg-white px-6">
			<Link href="/" className="flex items-center gap-2">
				<Image
					src="/funart.webp"
					alt="FunArt"
					width={32}
					height={32}
					className="rounded-xl"
				/>
				<span className="text-xl font-bold">FunArt</span>
			</Link>

			<nav className="flex items-center gap-6">
				{navItems.map((item) => {
					const Icon = item.icon;
					const isActive = pathname === item.url;
					return (
						<Link
							key={item.name}
							href={item.url}
							className={`flex items-center gap-2 text-sm font-medium ${
								isActive
									? "text-gray-900 dark:text-gray-100"
									: "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
							}`}
						>
							<Icon className="h-5 w-5" />
							{item.name}
						</Link>
					);
				})}
			</nav>

			{isAdminPanel ? (
				<Link
					href="/"
					className="text-sm text-gray-500 hover:text-gray-900 dark:hover:text-gray-100"
				>
					Back to Website
				</Link>
			) : user ? (
				<UserDropdown user={user as User} />
			) : (
				<StartCreatingButton />
			)}
		</header>
	);
}

export default function Header() {
	return (
		<div className="relative">
			<HeaderDesktop />
			<HeaderMobile />
		</div>
	);
}
